export type Period = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type DueOn = 'day-5' | 'day-10' | 'day-15' | 'end-of-month' | 'following-friday';
export type YearType = 'CY' | 'FY';

export type ScheduleRow = {
  schedule?: string;
  period?: Period;
  yearType?: YearType;
  dueOn?: DueOn;
  isRequired?: boolean;
};

export const PERIODS: Period[] = ['weekly', 'monthly', 'quarterly', 'yearly'];
export const YEAR_TYPES: YearType[] = ['CY', 'FY'];
/** Due dates a non-weekly schedule may pick; weekly is locked to `following-friday`. */
export const MONTH_DAY_DUE_ONS: DueOn[] = ['day-5', 'day-10', 'day-15', 'end-of-month'];

const WEEKLY_DUE_ON: DueOn = 'following-friday';
const DEFAULT_DUE_ON: DueOn = 'end-of-month';
const CALENDAR_YEAR: YearType = 'CY';

/** Year type only matters for quarterly and yearly; weekly and monthly are always calendar year. */
export function showsYearType(period?: Period): boolean {
  return period === 'quarterly' || period === 'yearly';
}

/**
 * Applies the frequency rules: weekly is due the following Friday, `following-friday` is valid only for weekly,
 * and weekly/monthly are always calendar year. Returns the same row when it already follows them.
 */
export function normalizeRow(row: ScheduleRow): ScheduleRow {
  const isWeekly = row.period === 'weekly';
  const dueOn = isWeekly ? WEEKLY_DUE_ON : row.dueOn === WEEKLY_DUE_ON ? DEFAULT_DUE_ON : row.dueOn;
  const yearType = isWeekly || row.period === 'monthly' ? CALENDAR_YEAR : row.yearType;

  if (dueOn === row.dueOn && yearType === row.yearType) return row;

  return { ...row, dueOn, yearType };
}

/** Switches the frequency; leaving weekly resets the due date to the end of the month. */
export function changePeriod(row: ScheduleRow, period: Period): ScheduleRow {
  const leavesWeekly = row.period === 'weekly' && period !== 'weekly';

  return normalizeRow({ ...row, period, dueOn: leavesWeekly ? DEFAULT_DUE_ON : row.dueOn });
}

/** Indexes of rows whose schedule and frequency repeat an earlier row. Rows without a schedule are never duplicates. */
export function duplicateIndexes(rows: ScheduleRow[]): Set<number> {
  const seen = new Set<string>();
  const duplicates = new Set<number>();

  rows.forEach((row, index) => {
    if (!row.schedule) return;
    const key = JSON.stringify([row.schedule, row.period]);
    if (seen.has(key)) duplicates.add(index);
    else seen.add(key);
  });

  return duplicates;
}

export function newRow(): ScheduleRow {
  return { period: 'monthly', yearType: CALENDAR_YEAR, dueOn: DEFAULT_DUE_ON, isRequired: false };
}
